import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { format, formatDistanceToNow } from 'date-fns';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';

import { useAuthContext } from '../../auth/hooks';
import { useBoolean } from '../../hooks/use-boolean';
import ConfirmationDialogue from '../confirmationDialogue';
import { useDjangoApi } from '../djangoServerUtilities/djangoHandler';

type Props = {
  comment: { id: string, note: string, created_at: string, created_by: string, updated_at: string, updated_by: string };
  reloadComments: () => void;
}

export default function CommentCard({ comment, reloadComments }: Props) {
  const { user } = useAuthContext();
  const djangoApi = useDjangoApi();
  const showDeletionDialogue = useBoolean(false);
  const editMode = useBoolean(false);
  const [edittedComment, setEdittedComment] = useState<string>(comment.note);

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (date.getDate() === new Date().getDate()) {
        return formatDistanceToNow(date, { addSuffix: true });
      } else {
        return format(date, 'MMMM do, yyyy');
      }
    } catch {
      return dateString;
    }
  };

  const isEdited = () => {
    if (!comment.created_at || !comment.updated_at) return false;
    const created = new Date(comment.created_at);
    const updated = new Date(comment.updated_at);
    // Consider edited if there's more than 1 minute difference
    return Math.abs(updated.getTime() - created.getTime()) > 60000;
  };

  const getEditTooltipText = () => {
    const date = new Date(comment.updated_at);
    if (date.getDate() === new Date().getDate()) {
      return `Edited ${formatDistanceToNow(date, { addSuffix: true })}`;
    } else {
      return `Edited on ${format(date, 'MMMM do, yyyy')}`;
    }
  };

  const deleteComment = async () => {
    const response = await djangoApi.patch(`/comments/${comment.id}/`, {
      is_deleted: true,
    }
    )
    if (response && response.status === 200) {
      enqueueSnackbar("Comment deleted")
    }
    else
      enqueueSnackbar("Unable to delete comment", { variant: 'error' });
    reloadComments()
  }

  const updateComment = async () => {
    const response = await djangoApi.patch(`/comments/${comment.id}/`, {
      note: edittedComment,
    }
    )
    if (response && response.status === 200) {
      enqueueSnackbar("Comment updated")
    }
    else
      enqueueSnackbar("Unable to update comment", { variant: 'error' });
    reloadComments()
  }

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          backgroundColor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            borderColor: 'primary.light',
          }
        }}
      >
        {/* Header with avatar and metadata */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              {comment.created_by ? getInitials(comment.created_by) : <PersonIcon fontSize="small" />}
            </Avatar>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.2
                }}
              >
                {comment.updated_by || 'N/A'}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem'
                  }}
                >
                  {formatDate(comment.created_at)}
                </Typography>
                {isEdited() && (
                  <Tooltip
                    title={getEditTooltipText()}
                    placement="bottom"
                    arrow
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        fontStyle: 'italic',
                        opacity: 0.8,
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                    >
                      • edited
                    </Typography>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Stack>

          {/* Action buttons */}
          {user && user.email === comment.created_by &&
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: alpha('#1976d2', 0.08)
                  }
                }}
                onClick={() => { setEdittedComment(comment.note); editMode.onToggle(); }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                    backgroundColor: alpha('#d32f2f', 0.08)
                  }
                }}
                onClick={showDeletionDialogue.onTrue}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        </Stack>

        {/* Comment content */}
        {editMode.value ?
          <>
            <TextField
              value={edittedComment}
              onChange={(e) => { setEdittedComment(e.target.value) }}
              sx={{ width: '100%' }}
              multiline
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  editMode.onFalse();
                  updateComment();
                }
              }}
            />
            <Stack direction='row' justifyContent='flex-end' sx={{ mt: 1 }}>
              <Button variant='soft' size='small' onClick={() => { editMode.onFalse(); updateComment(); }}>Submit Edit</Button>
            </Stack>
          </>
          :
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {comment.note}
          </Typography>
        }


      </Box>
      <ConfirmationDialogue methodToExecuteOnConfirmation={() => { showDeletionDialogue.onFalse(); deleteComment(); }} openDialog={showDeletionDialogue.value} closeDialog={showDeletionDialogue.onFalse} customMessage="" />
    </>
  )
}
